import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MaterialModule } from './common/material/material.module';
import { WestnetzModule } from './common/westnetz/westnetz.module';
import { OAuth2AuthGuard } from './service/auth/oauth2.guard';
import { AuthGuard } from './service/auth/auth.guard';
import { SessionInterceptor } from './service/auth/session.interceptor';
import { WelcomeComponent } from './welcome/welcome.component';
import { PipeModule } from './common/pipe/pipe.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { NetzzuordnungenModule } from './netzzuordnungen/netzzuordnungen.module';
import { SettingsModule } from './settings/settings.module';
import { RoleAuthGuard } from './service/auth/role-auth.guard';
import { NetzbetreiberModule } from './netzbetreiber/netzbetreiber.module';
import { ContactDataModule } from './contact-data/contact-data.module';
import { ImportWizardModule } from './ea-import-wizard/import-wizard.module';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { ErzeugungsanlageModule } from './erzeugungsanlage/erzeugungsanlage.module';
import { createErrorHandler } from '@sentry/angular';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { PlanwertModule } from './planwert/planwert.module';
import { FormComponentsModule } from './common/form/components/form-components.module';
import { FormsModule } from '@angular/forms';
import { ImportNzIdWizardModule } from './nzId-import-wizard/import-nz-id-wizard.module';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { GermanPaginatorIntl } from './common/material/german-paginator-intl';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { GermanDatepickerIntl } from './common/material/german-datepicker-intl';

registerLocaleData(localeDe);

const MY_FORMATS = {
    parse: {
        dateInput: 'L',
    },
    display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

const INTERCEPTORS = [
    SessionInterceptor,
];

const GUARDS = [
    AuthGuard,
    RoleAuthGuard,
    OAuth2AuthGuard,
];

@NgModule({
    declarations: [
        AppComponent,
        WelcomeComponent,
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        MaterialModule,
        ContactDataModule,
        NetzzuordnungenModule,
        NetzbetreiberModule,
        ImportWizardModule,
        ImportNzIdWizardModule,
        WestnetzModule,
        PipeModule,
        DashboardModule,
        SettingsModule,
        MatNativeDateModule,
        ErzeugungsanlageModule,
        PlanwertModule,
        FormComponentsModule,
        CommonModule,
        FormsModule,
    ],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
        // Run backend and frontend in the same timezone, otherwise the dates generated within
        // the excel in the backend might differ from the ones you put in from the frontend
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [
                MAT_DATE_LOCALE,
                MAT_MOMENT_DATE_ADAPTER_OPTIONS,
            ],
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        {
            provide: ErrorHandler,
            useValue: createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                hasBackdrop: true,
                ariaModal: true,
                restoreFocus: true,
                delayFocusTrap: true,
                autoFocus: 'first-tabbable',
            } as MatDialogConfig,
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'outline',
                floatLabel: 'always',
                subscriptSizing: 'dynamic',
            } as MatFormFieldDefaultOptions,
        },
        {
            provide: MatPaginatorIntl,
            useClass: GermanPaginatorIntl,
        },
        {
            provide: MatDatepickerIntl,
            useClass: GermanDatepickerIntl,
        },
        INTERCEPTORS,
        GUARDS,
    ],
    bootstrap: [
        AppComponent,
    ],
})
export class AppModule {
}
